<script lang="ts">
  import { Accordion } from "bits-ui";
  import { flushSync } from "svelte";
  import type { Snippet } from "svelte";
  import { page } from "$app/state";
  import { m } from "#messages";
  import { basePath, publicHref } from "#lib/locale.ts";
  import Icon from "#lib/ui/Icon.svelte";
  import IconButton from "#lib/ui/IconButton.svelte";
  import { ENTRANCE_FRAME, FOCUS_RING } from "#lib/ui/recipes.ts";
  import { PRICING, PRODUCTS, RESOURCES, WORKSPACE } from "./products.ts";
  import type { NavLink } from "./products.ts";

  let {
    account,
    brand,
    prepare,
  }: {
    account: Snippet;
    brand: Snippet<[() => void]>;
    prepare: () => Promise<void>;
  } = $props();

  let dialog: HTMLDialogElement | undefined = $state();
  let anchor: HTMLElement | undefined = $state();
  let shown = $state(false);
  let bar = $state<{ top: number; left: number; width: number; height: number } | null>(null);

  const open = async (): Promise<void> => {
    await prepare();
    const row = anchor?.closest("[data-entrance-bar]")?.getBoundingClientRect();
    bar =
      row === undefined
        ? null
        : { top: row.top, left: row.left, width: row.width, height: row.height };
    flushSync(() => (shown = true));
    dialog?.showModal();
    const edge = top?.getBoundingClientRect();
    side =
      edge === undefined || dialog === undefined || bar === null
        ? null
        : { left: `${edge.left}px`, right: `${dialog.clientWidth - edge.right}px` };
  };
  const close = (): void => dialog?.close();

  const here = $derived(basePath(page.url.pathname));
  type Plain = Pick<NavLink, "key" | "href" | "icon" | "away">;

  const current = (link: Plain): boolean =>
    link.away !== true && basePath(link.href.replace(/#.*/, "")) === here;

  const HEAD = "flex w-full items-center justify-between gap-3 py-3 text-xl font-medium text-ink-1";
  const CARD =
    "flex items-start gap-3 rounded-card border border-edge bg-surface-1 px-4 py-3 transition-colors duration-120 hover:bg-surface-2 active:bg-surface-2 aria-[current=page]:border-edge-strong";
  const ROW =
    "flex items-center gap-2.5 rounded-control px-2 py-2 text-md text-ink-2 transition-colors duration-120 hover:text-ink-1 aria-[current=page]:text-ink-1";

  let section = $state("");

  let top: HTMLElement | undefined = $state();
  let side = $state<{ left: string; right: string } | null>(null);
</script>

{#snippet card(link: NavLink)}
  <a
    href={link.away === true ? link.href : publicHref(link.href)}
    onclick={close}
    aria-current={current(link) ? "page" : undefined}
    class={[CARD, FOCUS_RING]}
    data-testid="mobile-{link.key}"
  >
    <span class="mt-0.5 flex text-ink-1"><Icon name={link.icon} size={18} /></span>
    <span class="flex min-w-0 flex-col gap-0.5">
      <span class="text-md font-medium text-ink-1">{link.label()}</span>
      <span class="text-sm leading-snug text-ink-2">{link.hint()}</span>
    </span>
  </a>
{/snippet}

{#snippet plain(link: Plain, label: string)}
  <a
    href={link.away === true ? link.href : publicHref(link.href)}
    onclick={close}
    aria-current={current(link) ? "page" : undefined}
    class={[ROW, FOCUS_RING]}
    data-testid="mobile-{link.key}"
  >
    <span class="flex"><Icon name={link.icon} size={16} /></span>
    {label}
  </a>
{/snippet}

{#snippet group(value: string, title: string, body: Snippet)}
  <Accordion.Item {value}>
    <Accordion.Header>
      <Accordion.Trigger class={[HEAD, FOCUS_RING]} data-testid="mobile-group-{value}">
        {title}
        <span
          class={[
            "flex size-8 shrink-0 items-center justify-center text-ink-2 transition-transform duration-200",
            section === value && "rotate-180",
          ]}
        >
          <Icon name="caretDown" size={14} />
        </span>
      </Accordion.Trigger>
    </Accordion.Header>
    <Accordion.Content class="group-body pb-3">{@render body()}</Accordion.Content>
  </Accordion.Item>
{/snippet}

{#snippet products()}
  <div class="flex flex-col gap-2">
    {#each PRODUCTS as link (link.key)}{@render card(link)}{/each}
  </div>
  <div class="mt-2 flex flex-col">
    {@render plain(WORKSPACE, WORKSPACE.label())}
  </div>
{/snippet}

{#snippet resources()}
  <div class="flex flex-col gap-2">
    {#each RESOURCES as link (link.key)}{@render card(link)}{/each}
  </div>
{/snippet}

<span bind:this={anchor} class="flex">
  <IconButton
    label={m.marketing_menu()}
    aria-haspopup="dialog"
    aria-controls="mobile-menu"
    onpointerenter={prepare}
    onfocus={prepare}
    onclick={open}
    data-testid="mobile-menu-open"
  >
    <Icon name="drawer" size={20} />
  </IconButton>
</span>

<dialog
  bind:this={dialog}
  id="mobile-menu"
  aria-label={m.marketing_menu()}
  class="sheet m-0 h-dvh max-h-none w-full max-w-none bg-surface-0 p-0 text-ink-1"
  data-testid="mobile-menu"
  onclose={() => (shown = false)}
>
  {#if shown}
    <div class="flex h-full flex-col">
      <div class="relative h-20 shrink-0">
        <div
          bind:this={top}
          class={["flex items-center justify-between", bar === null && [ENTRANCE_FRAME, "h-14"]]}
          style:position={bar === null ? null : "absolute"}
          style:top={bar === null ? null : `${bar.top}px`}
          style:left={bar === null ? null : `${bar.left}px`}
          style:width={bar === null ? null : `${bar.width}px`}
          style:height={bar === null ? null : `${bar.height}px`}
        >
          {@render brand(close)}
          <span class="dismiss flex">
            <IconButton
              label={m.ui_close()}
              aria-controls="mobile-menu"
              onclick={close}
              autofocus
              data-testid="mobile-menu-close"
            >
              <Icon name="dismiss" size={20} />
            </IconButton>
          </span>
        </div>
      </div>

      <nav
        class={[
          side === null && ENTRANCE_FRAME,
          "scrollbar-subtle flex flex-1 flex-col overflow-y-auto pt-2 pb-6",
        ]}
        style:padding-left={side?.left}
        style:padding-right={side?.right}
      >
        <Accordion.Root type="single" bind:value={section} class="part" style="--order: 0">
          {@render group("products", m.marketing_products(), products)}
          {@render group("resources", m.marketing_resources(), resources)}
        </Accordion.Root>
        <a
          href={publicHref(PRICING.href)}
          onclick={close}
          aria-current={current(PRICING) ? "page" : undefined}
          class={["part", HEAD, FOCUS_RING]}
          style:--order="1"
          data-testid="mobile-pricing"
        >
          {m.pricing_link()}
        </a>
      </nav>

      <div
        class={[side === null && ENTRANCE_FRAME, "part shrink-0 pt-3 pb-8"]}
        style:--order="2"
        style:padding-left={side?.left}
        style:padding-right={side?.right}
      >
        {@render account()}
      </div>
    </div>
  {/if}
</dialog>

<style>
  .sheet[open] {
    animation: sheet-in 180ms ease-out;
  }

  .sheet[open] .dismiss {
    animation: dismiss-in 220ms cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  :global(.group-body[data-state="open"]) {
    animation: part-in 220ms cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  .sheet[open] :global(.part) {
    animation: part-in 280ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
    animation-delay: calc(var(--order) * 40ms);
  }

  @keyframes sheet-in {
    from {
      opacity: 0;
    }
  }

  @keyframes dismiss-in {
    from {
      opacity: 0;
      rotate: -90deg;
    }
  }

  @keyframes part-in {
    from {
      opacity: 0;
      translate: 0 -0.5rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .sheet[open],
    .sheet[open] .dismiss,
    .sheet[open] :global(.part) {
      animation: none;
    }
  }
</style>
